import { appConfig } from '../app-config/AppConfig'
import axiosInstance from '../lib/api/axiosInstance'
import { clearAllDailyStats } from '../pages/DailyStats'
import { clearAllDashboard } from '../pages/Dashboard'
import { clearReports } from '../pages/Reports/ReportsSlice'
import { clearAllSettings } from '../pages/Settings/SettingsSlice'
import { clearAllTradingLog } from '../pages/TradingLog'
import globalStore from '../store'
import { clearAllJournalingProgress } from '../store/reducers/journalingReducerSlice'
import { clearAllAccount, UserPlatform } from '../user'

import TokenService from './TokenService'

type SessionHeartbeatResponse = {
  restarted: boolean
  sessionId: string
  accessToken?: string
  refreshToken?: string
}

class AuthenticationService {
  private heartbeatPromise: Promise<SessionHeartbeatResponse | undefined> | null = null

  authenticate = (email: string, password: string, platform: UserPlatform) => {
    return TokenService.retrieveTokenData(email, password, platform)
  }

  logout = async () => {
    const refreshToken = TokenService.getRefreshToken()

    try {
      await fetch(appConfig.REACT_APP_API_URL + '/auth/logout', {
        method: 'POST',
        credentials: 'include',
        headers: refreshToken
          ? {
              Authorization: `Bearer ${refreshToken}`,
            }
          : undefined,
      })
    } catch (error) {
      // ignore logout errors — token may be expired
    }

    TokenService.removeAll()
    globalStore.dispatch(clearAllTradingLog())
    globalStore.dispatch(clearAllDashboard())
    globalStore.dispatch(clearAllDailyStats())
    globalStore.dispatch(clearAllSettings())
    globalStore.dispatch(clearAllAccount())
    globalStore.dispatch(clearAllJournalingProgress())
    globalStore.dispatch(clearReports())
    axiosInstance.defaults.headers.common.Authorization = ''
    this.clearCookies()
  }

  heartbeatSession = async (platform?: UserPlatform) => {
    if (this.heartbeatPromise) {
      return this.heartbeatPromise
    }

    this.heartbeatPromise = axiosInstance
      .post<{
        success: boolean
        data: SessionHeartbeatResponse
      }>('/auth/session/heartbeat', platform ? { platform } : {})
      .then((response) => {
        if (response.data?.success && response.data.data?.restarted) {
          const { accessToken, refreshToken } = response.data.data

          if (accessToken && refreshToken) {
            axiosInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`
            TokenService.setAccessAndRefreshToken(accessToken, refreshToken)
          }
        }

        return response.data?.data
      })
      .finally(() => {
        this.heartbeatPromise = null
      })

    return this.heartbeatPromise
  }

  hasHeartbeatInFlight = () => {
    return Boolean(this.heartbeatPromise)
  }

  clearCookies = () => {
    const cookies = document.cookie.split(';')
    cookies.forEach((cookie) => {
      const cookieName = cookie.split('=')[0].trim()

      // Skip cookies that start with "progressierinstallbanner"
      if (cookieName.startsWith('progressierinstallbanner')) return

      // Clear other cookies
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`
    })
  }

  isAuth = () => !!TokenService.getAccessToken()
}

const AuthService = new AuthenticationService()

export default AuthService
